import React, { useState, useEffect } from 'react';
import { Table, Tag, Progress, Input, Select, Button, Space, Card, Statistic, Row, Col, message, Tooltip } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { getUploadedFiles } from '../api/customer';

const { Search } = Input;
const { Option } = Select;

const UploadedFilesList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ status: null, search: null });
  const [statistics, setStatistics] = useState({
    total: 0,
    completed: 0,
    failed: 0,
    processing: 0
  });

  const fetchFiles = async (page = 1) => {
    setLoading(true);
    try {
      const result = await getUploadedFiles(
        page,
        pagination.pageSize,
        filters.status,
        filters.search
      );
      setData(result.results);
      setPagination({
        ...pagination,
        current: result.current_page,
        total: result.count
      });
      
      // Calculate statistics
      calculateStatistics(result.results);
    } catch (error) {
      console.error('Error fetching files:', error);
      message.error('Failed to load uploaded files');
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (files) => {
    const stats = {
      total: files.length,
      completed: files.filter(f => f.status === 'completed').length,
      failed: files.filter(f => f.status === 'failed').length,
      processing: files.filter(f => ['pending', 'parsing', 'matching', 'creating', 'saving'].includes(f.status)).length
    };
    setStatistics(stats);
  };

  useEffect(() => {
    fetchFiles();
    
    // Auto-refresh every 5 seconds if there are processing files
    const interval = setInterval(() => {
      if (data.some(f => ['pending', 'parsing', 'matching', 'creating', 'saving'].includes(f.status))) {
        fetchFiles(pagination.current);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [filters]);

  const handleDownloadReport = async (record) => {
    try {
      message.loading({ content: 'Generating report...', key: 'download' });
      
      const response = await fetch(`http://localhost:8000/customer/uploaded-files/${record.id}/report/`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${record.file_name}_report.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        message.success({ content: 'Report downloaded successfully', key: 'download' });
      } else {
        throw new Error('Failed to download report');
      }
    } catch (error) {
      console.error('Error downloading report:', error);
      message.error({ content: 'Failed to download report', key: 'download' });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'success',
      failed: 'error',
      pending: 'default',
      parsing: 'processing',
      matching: 'processing',
      creating: 'processing',
      saving: 'processing',
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircleOutlined />;
    if (status === 'failed') return <CloseCircleOutlined />;
    return <SyncOutlined spin />;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  const columns = [
    {
      title: 'File Name',
      dataIndex: 'file_name',
      key: 'file_name',
      width: 200,
      ellipsis: true,
      render: (text) => <Tooltip title={text}><strong>{text}</strong></Tooltip>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) => (
        <Tag icon={getStatusIcon(status)} color={getStatusColor(status)}>
          {record.status_display}
        </Tag>
      )
    },
    {
      title: 'Progress',
      dataIndex: 'progress_percentage',
      key: 'progress',
      width: 150,
      render: (percent, record) => (
        <div>
          <Progress 
            percent={percent} 
            status={record.status === 'failed' ? 'exception' : record.status === 'completed' ? 'success' : 'active'}
            size="small"
          />
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.current_step || 'Waiting...'}
          </div>
        </div>
      )
    },
    {
      title: 'Records',
      key: 'records',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
            {record.total_records}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.processed_records} processed
          </div>
        </div>
      )
    },
    {
      title: 'Results',
      key: 'results',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <div style={{ fontSize: '12px' }}>
            <Tag color="green" style={{ margin: 0 }}>New: {record.new_unique_persons}</Tag>
          </div>
          <div style={{ fontSize: '12px' }}>
            <Tag color="blue" style={{ margin: 0 }}>Matched: {record.matched_to_existing}</Tag>
          </div>
          <div style={{ fontSize: '12px' }}>
            <Tag color="orange" style={{ margin: 0 }}>Merged: {record.duplicates_merged}</Tag>
          </div>
        </Space>
      )
    },
    {
      title: 'Time',
      key: 'time',
      width: 120,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            {new Date(record.created_at).toLocaleDateString()}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {new Date(record.created_at).toLocaleTimeString()}
          </div>
          {record.processing_time > 0 && (
            <div style={{ fontSize: '12px', color: '#999' }}>
              Duration: {formatDuration(record.processing_time)}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space>
          <Tooltip title="Download Report">
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              size="small"
              disabled={record.status !== 'completed'}
              onClick={() => handleDownloadReport(record)}
            >
              Report
            </Button>
          </Tooltip>
        </Space>
      )
    },
  ];

  return (
    <Card 
      title={
        <Space>
          <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Upload History</span>
          <Tag color="blue">{pagination.total} files</Tag>
        </Space>
      }
      extra={
        <Button 
          icon={<ReloadOutlined />} 
          onClick={() => fetchFiles(pagination.current)}
          loading={loading}
        >
          Refresh
        </Button>
      }
    >
      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Uploads"
              value={pagination.total}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Completed"
              value={statistics.completed}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Processing"
              value={statistics.processing}
              valueStyle={{ color: '#faad14' }}
              prefix={<SyncOutlined spin={statistics.processing > 0} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Failed"
              value={statistics.failed}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Space style={{ marginBottom: 16 }} size="middle">
        <Search
          placeholder="Search by file name"
          onSearch={(value) => setFilters({ ...filters, search: value || null })}
          style={{ width: 300 }}
          allowClear
          prefix={<SearchOutlined />}
        />
        <Select
          placeholder="Filter by status"
          style={{ width: 200 }}
          allowClear
          onChange={(value) => setFilters({ ...filters, status: value || null })}
          value={filters.status}
        >
          <Option value="pending">Pending</Option>
          <Option value="parsing">Parsing</Option>
          <Option value="matching">Matching</Option>
          <Option value="creating">Creating</Option>
          <Option value="saving">Saving</Option>
          <Option value="completed">Completed</Option>
          <Option value="failed">Failed</Option>
        </Select>
      </Space>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} files`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        onChange={(newPagination) => {
          setPagination(newPagination);
          fetchFiles(newPagination.current);
        }}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
      />
    </Card>
  );
};

export default UploadedFilesList;
